import { oc } from "@orpc/contract";
import * as z from "zod";

export const KNOWLEDGE_FILE_EXTENSIONS = [".pdf", ".docx", ".txt", ".md"] as const;
export const KNOWLEDGE_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/markdown",
] as const;
export const KnowledgeParseStatusSchema = z.enum([
  "pending",
  "processing",
  "ready",
  "failed",
  "cancelled",
]);
export const KnowledgeConnectionSchema = z.object({
  provider: z.string(),
  baseUrl: z.string(),
  apiKeySet: z.boolean(),
  enabled: z.boolean(),
  embeddingModelId: z.string(),
  rerankModelId: z.string(),
  summaryModelId: z.string(),
  visionModelId: z.string(),
  maxUploadSizeBytes: z.number(),
});
export const KnowledgeCollectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  botIds: z.array(z.string()),
  documentCount: z.number(),
  createdAt: z.string(),
});
export const KnowledgeDocumentSchema = z.object({
  id: z.string(),
  collectionId: z.string(),
  name: z.string(),
  title: z.string(),
  version: z.string(),
  parseStatus: KnowledgeParseStatusSchema,
  currentStage: z.string().nullable(),
  error: z.string().nullable(),
  publicationStatus: z.enum(["draft", "published", "withdrawn"]),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  size: z.number(),
  sourceUrl: z.string(),
  replacesDocumentId: z.string().nullable(),
});
export type KnowledgeConnectionView = z.infer<typeof KnowledgeConnectionSchema>;
export type KnowledgeCollectionView = z.infer<typeof KnowledgeCollectionSchema>;
export type KnowledgeDocumentView = z.infer<typeof KnowledgeDocumentSchema>;
const DocumentInput = z.object({ documentId: z.string().min(1).max(200) });
export const knowledgeContract = {
  getConnection: oc.output(KnowledgeConnectionSchema.nullable()),
  saveConnection: oc
    .input(
      z.object({
        provider: z.literal("weknora"),
        baseUrl: z.string().url().max(2048),
        apiKey: z.string().min(1).max(8192).optional(),
        embeddingModelId: z.string().max(200).optional(),
        rerankModelId: z.string().max(200).optional(),
        summaryModelId: z.string().max(200).optional(),
        visionModelId: z.string().max(200).optional(),
        enabled: z.boolean().optional(),
      }),
    )
    .output(KnowledgeConnectionSchema),
  listCollections: oc.output(z.array(KnowledgeCollectionSchema)),
  createCollection: oc
    .input(
      z.object({
        name: z.string().trim().min(1).max(160),
        description: z.string().max(2000).optional(),
      }),
    )
    .output(KnowledgeCollectionSchema),
  listDocuments: oc
    .input(z.object({ collectionId: z.string().min(1).max(200) }))
    .output(z.array(KnowledgeDocumentSchema)),
  refreshDocument: oc.input(DocumentInput).output(KnowledgeDocumentSchema),
  retryDocument: oc.input(DocumentInput).output(KnowledgeDocumentSchema),
  publishDocument: oc.input(DocumentInput).output(KnowledgeDocumentSchema),
  withdrawDocument: oc.input(DocumentInput).output(KnowledgeDocumentSchema),
  setBindings: oc
    .input(
      z.object({
        collectionId: z.string().min(1).max(200),
        botIds: z.array(z.string().min(1).max(200)).max(100),
      }),
    )
    .output(z.object({ ok: z.literal(true) })),
};
